import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { ReAuthPopupService } from './services/reauth-popup.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { CVANotificationService } from '../../../services/cva-notification/cva-notification.service';
import { LoginService } from '../../../services/login/login.service';
import { CVA_CONSTANTS } from '../../../util/constants';
import { NotificationClassEnum } from '../../../models/notification-class.enum';
import { ROUTE_CONSTANTS } from '../../../util/route.constants';
import { CommonModule } from '@angular/common';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';

@Component({
    standalone: true,
    selector: 'cva-standalone-reauth-popup',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './reauth-popup.component.html',
    styleUrl: './reauth-popup.component.scss'
})
export class ReauthPopupComponent implements OnInit {
  @ViewChild('reAuthNgForm') reAuthNgForm: NgForm;

  reAuthForm: UntypedFormGroup;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly reAuthPopupService: ReAuthPopupService,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly loginService: LoginService,
    private readonly router: Router,
    private readonly dialogRef: MatDialogRef<ReauthPopupComponent>,
    private recaptchaV3Service: ReCaptchaV3Service,
    @Inject(MAT_DIALOG_DATA) public data: { emailId: string }
  ) { }

  ngOnInit(): void {
    const email = this.data?.emailId;
    if (!email) {
      this.finalLogout();
      console.error("Unable to fetch user email")
    }
    this.reAuthForm = this.formBuilder.group({
      email: [email, [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  reAuthenticate(): void {
    if (this.reAuthForm.invalid) {
      return;
    }
    this.recaptchaV3Service.execute('loginAction').subscribe({
      next: (token: string) => {
        this.reAuthForm.disable();

        this.loginService.sessionLogin({ ...this.reAuthForm.value, token, action: 'loginAction' })
          .pipe(
            finalize(() => {
              // Re-enable the form
              this.reAuthForm.enable();
              this.reAuthForm.markAsUntouched();
            })
          )
          .subscribe({
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            next: (res: any) => {
              console.log("Re-login successful!");

              this.reAuthForm.reset();

              const redirectUrl = res.redirectUrl;
              if (redirectUrl) {
                this.reAuthPopupService.saveTokenOnReLogin(redirectUrl);
                this.dialogRef.close(true);
              } else {
                this.finalLogout();
              }
            }, error: (err) => {
              console.log("Re-login failed");
              // Reset the form
              this.reAuthForm.patchValue({ password: '' });

              this.cvaNotificationService.showNotification(
                err?.error?.message || CVA_CONSTANTS.MESSAGE.UNKNOWN_LOGIN_ERR,
                NotificationClassEnum.ERROR,
                8000
              );

              if (err?.error?.resetLink) {
                this.loginService.redirectToResetPassword(err?.error?.resetLink);
                this.dialogRef.close(false);
              } else if (err?.error?.isPermanentLockout) {
                this.finalLogout();
              }
            }
          });
      }, error: (error: unknown) => {
        console.log("Error while fetching reCAPTCHA token");
        const message =
          (error instanceof Error ? error.message : (error as { message?: string })?.message) ||
          'Error occurred while validating CAPTCHA';
        this.cvaNotificationService.showNotification(
          message,
          NotificationClassEnum.ERROR,
          8000
        );
      }
    })


  }

  finalLogout() {
    this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
    this.dialogRef.close(false);
  }
}
